import { useMemo } from "react";
import moment from "moment";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { Row, Col } from "react-bootstrap";
import { useParams } from "react-router-dom";

import Card from "components/molecules/Card/Card";
import Hr from "components/atoms/Hr/Hr";
import UnitConverter from "components/organisms/UnitConverter/UnitConverter";
import CardNew from "components/molecules/Card/CardNew";
import { formatNumber, formatWeight } from "helpers/commonHelpers";
import useCountryNameByIsoCode from "hooks/useCountryNameByIsoCode";
import { calculateGoodItemsTotalWeight } from "helpers/quotationFormHelper";
import { useGetPackingListConfig } from "api/queries/location/useGetPackingListConfig";
import { useGetQuickRequestDetails } from "api/queries/quick-request/useGetQuickRequestDetails";
import usePermissions from "hooks/usePermissions";

import nmfcClasses from "./nmfcClasses.json";

const QuickPackingList = function ({ data, loadingData, quickRequestReference, isDrawer }) {
  const intl = useIntl();
  const getCountryName = useCountryNameByIsoCode();
  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");
  const isProvider = user_type === "provider";

  const { id } = useParams();
  const quoteRequestReference = data?.organization?.quick_request_reference;

  const { data: quickDetails } = useGetQuickRequestDetails(id ?? quoteRequestReference, {
    enabled: !!id || !!quoteRequestReference,
  });

  const { data: packingListConfig } = useGetPackingListConfig(quickDetails?.organization?.payment_location_reference, {
    enabled: !isProvider && !!quickDetails?.organization?.payment_location_reference,
    refetchOnMount: false,
  });

  const handlingUnitHashMap = useMemo(() => {
    if (packingListConfig?.handling_unit_options && packingListConfig?.handling_unit_options?.length) {
      const handlingUnitData = {};
      packingListConfig.handling_unit_options.forEach((item) => {
        if (Object.keys(item)?.[0]) {
          handlingUnitData[Object.keys(item)?.[0].toLowerCase()] = item[Object.keys(item)?.[0]];
        }
      });
      return handlingUnitData;
    }
    return {};
  }, [packingListConfig?.handling_unit_options]);

  const { good_items, goods_confirmation_type } = data?.organization || {};

  const { totalAmount, totalWeight, totalCBM, pieces } = useMemo(() => {
    let accumulateAmount = 0;
    let accumulateCBM = 0;
    let accumulatePieces = 0;
    let currencyCode = "";
    if (good_items?.length > 0) {
      good_items.forEach((item) => {
        accumulatePieces += item.units;
        accumulateCBM += item.total_cbm;
        if (item.good_customs_information?.length) {
          item.good_customs_information.forEach((customs_info) => {
            accumulateAmount += customs_info.value;
            if (customs_info.currency_code) currencyCode = customs_info.currency_code;
          });
        }
      });
    }
    const { weight, unit } = calculateGoodItemsTotalWeight(good_items);
    return {
      totalAmount: `${new Intl.NumberFormat("en-IN", {}).format(accumulateAmount).toString()} ${currencyCode}`,
      totalWeight: { weight, unit },
      pieces: accumulatePieces,
      totalCBM: accumulateCBM,
    };
  }, [good_items]);

  const dangerousGoodsOptions = [
    {
      label: intl.formatMessage({
        id: "new-request.packing-list.selectType",
        defaultMessage: "Select type",
      }),
      value: "",
    },
    {
      label: intl.formatMessage({
        id: "new-request.packing-list.dangerousGoodsOption1",
        defaultMessage: "1. Explosive substances and articles",
      }),
      value: "1",
    },
    {
      label: intl.formatMessage({
        id: "new-request.packing-list.dangerousGoodsOption2",
        defaultMessage: "2. Gasses",
      }),
      value: "2",
    },
    {
      label: intl.formatMessage({
        id: "new-request.packing-list.dangerousGoodsOption3",
        defaultMessage: "3. Flammable liquids",
      }),
      value: "3",
    },
    {
      label: intl.formatMessage({
        id: "new-request.packing-list.dangerousGoodsOption4",
        defaultMessage: "4. Flammable solids",
      }),
      value: "4",
    },
    {
      label: intl.formatMessage({
        id: "new-request.packing-list.dangerousGoodsOption5",
        defaultMessage: "5. Oxidising substances",
      }),
      value: "5",
    },
    {
      label: intl.formatMessage({
        id: "new-request.packing-list.dangerousGoodsOption6",
        defaultMessage: "6. Toxic & Infectious substances",
      }),
      value: "6",
    },
    {
      label: intl.formatMessage({
        id: "new-request.packing-list.dangerousGoodsOption7",
        defaultMessage: "7. Radioactive material",
      }),
      value: "7",
    },
    {
      label: intl.formatMessage({
        id: "new-request.packing-list.dangerousGoodsOption8",
        defaultMessage: "8. Corrosives",
      }),
      value: "8",
    },
    {
      label: intl.formatMessage({
        id: "new-request.packing-list.dangerousGoodsOption9",
        defaultMessage: "9. Miscellaneous",
      }),
      value: "9",
    },
  ];

  return (
    <CardNew
      className={`${isDrawer ? "" : "mb-15"} font-b3`}
      isLoading={loadingData}
      title={quickRequestReference || <FormattedMessage id="general.packingList" defaultMessage="Packing list" />}
    >
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quote-request-detail.totalPackages" defaultMessage="Total packages" />
        </Col>
        <Col>{pieces}</Col>
      </Row>
      {!isNaN(totalWeight?.weight) && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.totalWeight" defaultMessage="Total weight" />
          </Col>
          <Col>
            <UnitConverter
              content={<span>{formatWeight(totalWeight.weight.toFixed(2), totalWeight.unit)}</span>}
              unit={totalWeight.unit}
              value={totalWeight.weight.toFixed(2)}
            />
          </Col>
        </Row>
      )}
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="new-request.quote-confirmation.packing-list.CBM" defaultMessage="Total CBM" />
        </Col>
        <Col>{parseFloat(totalCBM).toFixed(2)} m³</Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quote-request-detail.totalCustomsValue" defaultMessage="Total customs value" />
        </Col>
        <Col>{totalAmount}</Col>
      </Row>
      {goods_confirmation_type && (
        <Row className="mb-2">
          <Col>
            <span className="text-secondary">
              <FormattedMessage id="new-request.organisational-GoodListEstimation" defaultMessage="Goods list estimation" />
            </span>
          </Col>
          <Col>{goods_confirmation_type || "-"}</Col>
        </Row>
      )}

      {!isDrawer && <Hr />}

      {good_items &&
        good_items.map((item, index) => {
          const {
            handling_unit,
            length,
            width,
            height,
            m_unit,
            weight,
            w_unit,
            units,
            stackable,
            hazardous,
            crating_required,
            crating_required_by_date,
            is_dangerous_good,
            dangerous_good_value,
            un_number,
            container_type,
            temperature_control_required,
            temperature_control_value,
            additional_information,
            good_customs_information,
          } = item;

          // total custom amount;
          let totalAmount = 0;
          return (
            <div key={Math.random()}>
              <div className="mb-4">
                <FormattedMessage id="quote-request-detail.commodity" defaultMessage="Commodity" /> {index + 1}
              </div>
              <Row className="mb-2">
                <Col className="text-secondary">
                  <FormattedMessage id="new-request.packing-list.handlingUnit" defaultMessage="Handling unit" />
                </Col>
                <Col className="text-capitalize">
                  {handling_unit === "container" ? `${handling_unit} ${container_type}` : handling_unit}
                </Col>
              </Row>

              <Row className="mb-2">
                <Col className="text-secondary">LxWxH</Col>
                <Col className="d-flex">
                  {length && width && height && m_unit ? (
                    <>
                      <UnitConverter content={length} unit={m_unit} value={length} />
                      <span className="mx-1">x</span>
                      <UnitConverter content={width} unit={m_unit} value={width} />
                      <span className="mx-1">x</span>
                      <UnitConverter content={height} unit={m_unit} value={height} />
                      <span className="mx-1">{m_unit}</span>
                    </>
                  ) : (
                    "-"
                  )}
                </Col>
              </Row>
              {item.other_instructions && (
                <Row className="mb-2">
                  <Col className="text-secondary">
                    {handlingUnitHashMap?.[item?.handling_unit?.toLowerCase?.()]?.additional_field_label ||
                      intl.formatMessage({
                        id: "new-request.packing-list.otherInstructions",
                        defaultMessage: "Other instructions",
                      })}
                  </Col>
                  <Col>{item.other_instructions}</Col>
                </Row>
              )}
              <Row className="mb-2">
                <Col className="text-secondary">
                  <FormattedMessage id="new-request.packing-list.unitWeight" defaultMessage="Unit weight" />
                </Col>
                <Col>
                  {weight ? (
                    <UnitConverter
                      content={<span>{formatWeight(weight.toFixed(2), w_unit)}</span>}
                      unit={w_unit}
                      value={weight.toFixed(2)}
                    />
                  ) : (
                    "-"
                  )}
                </Col>
              </Row>

              <Row className="mb-2">
                <Col className="text-secondary">
                  <FormattedMessage id="new-request.packing-list.numberOfUnits" defaultMessage="Quantity" />
                </Col>
                <Col>{units || "-"}</Col>
              </Row>

              {item.handling_unit !== "container" && (
                <Row className="mb-2">
                  <Col className="text-secondary">
                    <FormattedMessage id="quote-request-detail.stackable" defaultMessage="Stackable" />
                  </Col>
                  <Col>
                    {stackable
                      ? intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" })
                      : intl.formatMessage({ id: "general.no", defaultMessage: "No" })}
                  </Col>
                </Row>
              )}

              {typeof hazardous === "boolean" && (
                <Row className="mb-2">
                  <Col className="text-secondary">
                    <FormattedMessage id="new-request.packing-list.hazardous" defaultMessage="Hazardous" />
                  </Col>
                  <Col>
                    {hazardous
                      ? intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" })
                      : intl.formatMessage({ id: "general.no", defaultMessage: "No" })}
                  </Col>
                </Row>
              )}
              {crating_required && (
                <Row className="mb-2">
                  <Col className="text-secondary">
                    <FormattedMessage id="new-request.packing-list.cratingRequired" defaultMessage="Crating required" />
                  </Col>
                  <Col>
                    {crating_required
                      ? `${intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" })}  (by ${moment(
                          crating_required_by_date,
                          "YYYY-MM-DD",
                        ).format("DD MMM YYYY")})`
                      : intl.formatMessage({ id: "general.no", defaultMessage: "No" })}
                  </Col>
                </Row>
              )}
              {typeof is_dangerous_good === "boolean" && (
                <Row className="mb-2">
                  <Col className="text-secondary">
                    <FormattedMessage id="new-request.packing-list.dangerousGoods" defaultMessage="Dangerous goods" />
                  </Col>
                  <Col>
                    {is_dangerous_good
                      ? `${intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" })} - ${
                          dangerousGoodsOptions.find((item) => item.value === dangerous_good_value)?.label ?? ""
                        }${un_number ? ` (${un_number})` : ""}`
                      : intl.formatMessage({ id: "general.no", defaultMessage: "No" })}
                  </Col>
                </Row>
              )}
              {item.nmfc_class && (
                <Row className="mb-2">
                  <Col className="text-secondary">
                    <FormattedMessage id="new-request.packing-list.NMFC" defaultMessage="NMFC Class" />
                  </Col>
                  <Col>
                    {item.nmfc_class}
                    {item.nmfc_class && (
                      <span className="text-secondary font-weight-normal font-italic ml-2">
                        e.g. {nmfcClasses.find((c) => c.label === item.nmfc_class)?.value}
                      </span>
                    )}
                  </Col>
                </Row>
              )}
              {temperature_control_required && temperature_control_value && (
                <Row className="mb-2">
                  <Col className="text-secondary">
                    <FormattedMessage
                      id="new-request.packing-list.temperatureControl"
                      defaultMessage="Temperature controlled"
                    />
                  </Col>
                  <Col>{(temperature_control_required, temperature_control_value || "-")}</Col>
                </Row>
              )}
              {additional_information && (
                <Row className="mb-2">
                  <Col className="text-secondary">
                    <FormattedMessage
                      id="quote-request-detail.additionalInformation"
                      defaultMessage="Additional information"
                    />
                  </Col>
                  <Col>{additional_information || "-"}</Col>
                </Row>
              )}

              {good_customs_information?.length > 0 && (
                <Row className="mb-2">
                  <Col className="text-secondary">
                    <FormattedMessage
                      id="new-request.quote-confirmation.packing-list.customInformation"
                      defaultMessage="Customs information"
                    />
                  </Col>
                  <Col>
                    {good_customs_information.map(({ description, value, currency_code, hs_code, origin_country }, i) => {
                      totalAmount += value;
                      return (
                        <div key={`${description}_${Math.random()}`}>
                          <Card.Text>
                            {description}
                            <span className="ml-auto">
                              {formatNumber(value)} {currency_code}
                            </span>
                          </Card.Text>
                          {hs_code && (
                            <Card.Text className="mb-2">
                              <FormattedMessage id="new-request.packing-list.HsCode" defaultMessage="HS Code" />
                              <span className="ml-auto">{hs_code}</span>
                            </Card.Text>
                          )}
                          {origin_country && (
                            <Card.Text className="mb-2">
                              <FormattedMessage
                                id="new-request.packing-list.originCountry"
                                defaultMessage="Origin Country"
                              />
                              <span className="ml-auto">{getCountryName(origin_country)}</span>
                            </Card.Text>
                          )}

                          {i === good_customs_information.length - 1 && (
                            <>
                              <Hr />
                              <Row>
                                <Col>
                                  <FormattedMessage
                                    id="new-request.quote-confirmation.packing-list.totalCustomValue"
                                    defaultMessage="Total value"
                                  />
                                </Col>
                                <Col className="text-right">{`${formatNumber(totalAmount)} ${currency_code}`}</Col>
                              </Row>
                              <Hr />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </Col>
                </Row>
              )}
              {index !== good_items.length - 1 && <Hr />}
            </div>
          );
        })}
    </CardNew>
  );
};

QuickPackingList.propTypes = {
  data: PropTypes.instanceOf(Object),
  loadingData: PropTypes.bool.isRequired,
  isDrawer: PropTypes.bool,
  quickRequestReference: PropTypes.string,
};

QuickPackingList.defaultProps = {
  data: {},
  isDrawer: false,
  quickRequestReference: null,
};

export default QuickPackingList;
